import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Form, Formik } from 'formik';
import { Box, Stack } from '@mui/material';

import { getAllPodsState } from '../../../../../../../features/pod/get_all_pods';
import useNotification from '../../../../../../../core/hooks/use_notification';
import { TwoLevelSteps } from '../../../../../common/steps';
import { Button } from '../../../../../../common/button';
import ConfirmDialog from '../../../../../common/confirm_dialog';
import ProductsList from './products_list';
import _ from 'lodash';
import { GET_ROUTE_CUSTOMER, MISC_DATE_ISO_FORMAT } from '../../../../../../../core/constants';
import AddProductsDialog from './add_products_dialog';
import { createPodOrderNotificationData } from '../../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../../core/enums/common/api';
import { createPodOrderAction, createPodOrderState } from '../../../../../../../features/order/create_pod_order';
import SectionPaper from '../../../../../../common/section_paper';
import BottomActionBar from '../../../../../../common/bottom_action_bar';

const ProductsListForm = ({ customerId, form, setView, warehouseId }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();
    const navigate = useNavigate();
    const location = useLocation();

    const getAllPods = useSelector(getAllPodsState);
    const createPodOrder = useSelector(createPodOrderState);
    const pod = getAllPods?.data?.find(pod => pod?.id === form?.podId);

    const initialValues = {
        positions: []
    };

    const onReturn = () => setView(0);

    const onSubmit = (formik) => {
        const errors = validate(formik.values);

        if (errors.positions.length ||
            errors.products.length) {
            formik.setErrors(errors);
            return;
        }

        const formToSend = {
            ..._.omit(form, ['orderDate']),
            orderDate: form?.orderDate?.format(MISC_DATE_ISO_FORMAT),
            customerId,
            positions: formik?.values?.positions?.map(product => (
                {
                    ..._.omit(product, ['expirationDate', 'name', 'type', 'position'])
                }
            ))
        };

        dispatch(createPodOrderAction(formToSend))
            .then(response => {
                showNotification(createPodOrderNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    navigate(GET_ROUTE_CUSTOMER(customerId), { state: { tab: location?.state?.tab } });
                }
            });
    };

    const validate = (values) => {
        const errors = {
            positions: [],
            products: [],
            quantityErrors: {}
        };

        if (values.positions.length === 0) {
            errors.positions = 'Wybierz minimum 1 produkt';
        } else {
            values.positions.forEach((item, itemIndex) => {
                if (!item?.quantity) {
                    errors.products[itemIndex] = {
                        ...errors.products[itemIndex],
                        quantity: 'Pole wymagane'
                    };
                } else {
                    if (item?.quantity <= 0) {
                        errors.products[itemIndex] = {
                            ...errors.products[itemIndex],
                            quantity: 'Proszę podać wartość całkowitą dodatnią'
                        };
                    }
                }
            });

            if (errors.positions.length || errors.products.length) return errors;

            values.positions.filter(product => product?.productBatchId != null)
                .forEach((product, productIndex) => {
                    const productForValidation = values?.productsForValidation?.find(pfv => (pfv?.productId === product?.productId) && (pfv?.productBatchId === product?.productBatchId));

                    const productsSum = values.positions.flat()
                        .filter(p => (p?.productId === product?.productId) && (p?.productBatchId === product?.productBatchId))
                        .map(p => p.quantity)
                        .reduce((accumulator, current) => accumulator + current, 0);

                    if (productForValidation?.quantity < productsSum) {
                        errors.products[productIndex] = {
                            ...errors.products[productIndex],
                            quantity: `Dostępne ${ productForValidation.quantity } szt. na magazynie`
                        };
                    }
                });
        }

        return errors;
    };

    return (
        <Formik initialValues={ initialValues }>
            { (formik) => (
                <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1, pt: 2 } }>
                    <TwoLevelSteps secondStep/>

                    <Box sx={ { flexGrow: 1, pb: 4 } }>
                        <SectionPaper title="Lista produktów">
                            <ProductsList formik={ formik }/>

                            <Stack direction="row" spacing={ 2 } sx={ { mt: 3 } }>
                                <AddProductsDialog
                                    formik={ formik }
                                    warehouseId={ warehouseId }
                                    text={ 'Dodaj z urządzenia' }
                                />
                                {
                                    pod?.supportDynamicResources &&
                                    <AddProductsDialog
                                        formik={ formik }
                                        text={ 'Dodaj produkty' }
                                        allProducts
                                    />
                                }
                            </Stack>
                        </SectionPaper>
                    </Box>

                    <BottomActionBar>
                        <Box sx={ {
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                            alignItems: 'center'
                        } }>
                            <Button variant="outlined" onClick={ onReturn }>Wróć</Button>

                            <ConfirmDialog
                                isLoading={ createPodOrder?.loading }
                                title={ 'Czy na pewno chcesz utworzyć zakupy w imieniu użytkownika? Akcja zakończy się wysłaniem maila do klienta z informacją o płatności.' }
                                cancelText={ 'Anuluj' }
                                primaryAction={ () => onSubmit(formik, false) }
                                primaryText={ 'Utwórz' }
                                displayIcon={ false }
                                buttonText={ 'Zakończ i utwórz' }
                                maxWidth={ 'sm' }
                            />
                        </Box>
                    </BottomActionBar>
                </Box>
            ) }
        </Formik>
    );
};

export default ProductsListForm;